import {
  EnumDestinatarioTipoDestinatarioEnum,
  EnumTipoMittenteDTOCodice,
} from '@framework/api-client';
import { assertNever } from '@framework/engine';
import { t } from '@framework/i18n';
import { getNameInitials } from '@framework/ui';
import { Tag, TagProps } from 'antd';
import styled from 'styled-components';

const StyledTag = styled(Tag)<{ $mini?: boolean }>`
  text-transform: uppercase;
  letter-spacing: 0.03em;
  /* font-weight: 500; */
  line-height: ${props => (props.$mini ? '14px' : '20px')};
  padding-inline: ${props => (props.$mini ? '4px' : '7px')};
  &&& {
    font-weight: 600;
    // per contrasto accessibilità A11Y
    // valutare se inserire in UI
    &.ant-tag-green {
      color: #236407;
      background: #f6ffed;
      border-color: #b7eb8f;
    }
    &.ant-tag-orange {
      color: #9b4d03;
      background: #fff7e6;
      border-color: #ffd591;
    }
    &.ant-tag-cyan {
      color: #06767a;
      background: #e6fffb;
      border-color: #87e8de;
    }
  }
`;

/**
 * Ritorna le impostazioni per il tag
 *
 */
function getTagOptions(
  type:
    | EnumTipoMittenteDTOCodice
    | EnumDestinatarioTipoDestinatarioEnum
    | undefined,
): TagProps {
  if (!type)
    return {
      color: '#cf1322',
      children: t('communication-tag-option.no-type', 'N/D'),
    };
  switch (type) {
    case 'RICHIEDENTE':
      return {
        color: undefined,
        children: t('communication-tag-option.applicant', 'RICHIEDENTE'),
      };
    case 'TITOLARE':
      return {
        color: 'blue',
        children: t('communication-tag-option-holder', 'TITOLARE'),
      };
    case 'GESTORE':
      return {
        color: 'blue',
        children: t('communication-tag-option.manager', 'GESTORE'),
      };
    case 'PROCURATORE':
      return {
        color: 'blue',
        children: t('communication-tag-option.prosecutor', 'PROCURATORE'),
      };
    case 'RESPONSABILE_PROCEDIMENTO':
      return {
        color: 'green',
        children: t(
          'communication-tag-option.responsible-office',
          'UFFICIO RICEVENTE',
        ),
      };
    case 'ENDOPROCEDIMENTI_PRIMO_LIVELLO':
      return {
        color: 'orange',
        children: t(
          'communication-tag-option.third-organization',
          'ENTE TERZO',
        ),
      };
    case 'DELEGATI_COMPILAZIONE':
      return {
        color: 'cyan',
        children: t(
          'communication-tag-option.delegate-for-comp',
          'DELEGATO ALLA COMPILAZIONE',
        ),
      };
    case 'DELEGATI_FIRMA':
      return {
        color: 'purple',
        children: t(
          'communication-tag-option.delegate-for-sign',
          'DELEGATO ALLA FIRMA',
        ),
      };
    case 'ALTRO_UFFICIO_VARIABILE':
      return {
        color: 'purple',
        children: t(
          'communication-tag-option.other-variable-office',
          'UFFICIO REFERENTE',
        ),
      };
    case 'CO_TITOLARE':
    case 'CO_TITOLARI':
      return {
        color: 'blue',
        children: t('communication-tag-option.co-holder', 'PARTE INTERESSATA'),
      };
  }
  assertNever(type);
}
function getTagOrCodeOptions(
  type:
    | EnumTipoMittenteDTOCodice
    | EnumDestinatarioTipoDestinatarioEnum
    | undefined,
  generate?: boolean,
) {
  const options = getTagOptions(type);
  return generate
    ? { ...options, children: getNameInitials(options.children as string) }
    : options;
}

export interface ICommunicationUserTypeTagProps {
  type?:
    | EnumTipoMittenteDTOCodice
    | EnumDestinatarioTipoDestinatarioEnum
    | null;
  /** Versione compatta */
  mini?: boolean;
  /** Versione solo Iniziali */
  codeOnly?: boolean;
}

/**
 * Tipo di utente della comunicazione
 */
export function CommunicationUserTypeTag(
  props: ICommunicationUserTypeTagProps,
) {
  const { type, mini, codeOnly } = props;
  if (!type) return null;

  return <StyledTag $mini={mini} {...getTagOrCodeOptions(type, codeOnly)} />;
}
